import { useState, useCallback, useEffect } from 'react'

import { useFeatureFlagVariantKey } from 'posthog-js/react'

import GetStartedPlansConfig from '@config/get-started-plans-config'
import { setClarityFunnelTag } from '@lib/analytics/clarity'

import type { CollectionProducts, GetStartedPlansConfig as GetStartedPlansConfigType } from '@lib/types'
import type { AnnouncementConfig } from '@lib/new-survey/get-new-survey'
import type { GetStartedOffer } from '@lib/types/get-started-offer'
import type { GetStartedPageContent } from '@lib/types/get-started-page'
import { FALLBACK_PAGE_CONTENT } from '@lib/types/get-started-page'

import GetStartedAuth from './GetStartedAuth'
import GetStartedCheckout from './GetStartedCheckout'
import GetStartedLanding from './GetStartedLanding'
import GetStartedLandingVariant from './GetStartedLandingVariant'
import GetStartedPlanSelection from './GetStartedPlanSelection'
import GetStartedSurvey from './GetStartedSurvey'

type FlowStep = 'landing' | 'survey' | 'auth' | 'plan' | 'checkout'

type GetStartedContentProps = {
	announcement?: AnnouncementConfig | null
	collectionProducts?: CollectionProducts
	offer?: GetStartedOffer | null
	pageContent?: GetStartedPageContent
}

export default function GetStartedContent({ announcement, collectionProducts, offer, pageContent: pageContentProp }: GetStartedContentProps) {
	const pageContent = pageContentProp ?? FALLBACK_PAGE_CONTENT
	const config = GetStartedPlansConfig as GetStartedPlansConfigType
	const checkoutEnabled = config.checkoutStep.enabled
	const landingEnabled = config.landingStep?.enabled ?? false

	const activeOffer: GetStartedOffer | null = offer ?? null

	const [currentStep, setCurrentStep] = useState<FlowStep>(
		landingEnabled ? 'landing' : 'survey'
	)

	// Lock the variant on first resolve to prevent mid-funnel flag flip after posthog.identify()
	const flagVariant = useFeatureFlagVariantKey('survey-landing-clarity')
	const [lockedVariant, setLockedVariant] = useState<string | null | undefined>(undefined)
	useEffect(() => {
		if (lockedVariant !== undefined) return
		if (flagVariant === undefined) return
		setLockedVariant(typeof flagVariant === 'string' ? flagVariant : null)
	}, [flagVariant, lockedVariant])
	const activeVariant = lockedVariant ?? 'control'

	useEffect(() => {
		setClarityFunnelTag()
	}, [])

	const handleLandingComplete = useCallback(() => {
		setCurrentStep('survey')
	}, [])

	const handleSurveyComplete = useCallback(() => {
		setCurrentStep('auth')
	}, [])

	const handleAuthComplete = useCallback(() => {
		setCurrentStep('plan')
	}, [])

	const handlePlanComplete = useCallback(() => {
		setCurrentStep('checkout')
	}, [])

	return (
		<div className="bg-white min-h-screen">
			{currentStep === 'landing' && activeVariant === 'variant' && (
				<GetStartedLandingVariant
					onComplete={handleLandingComplete}
					collectionProducts={collectionProducts}
					pageContent={pageContent}
				/>
			)}

			{currentStep === 'landing' && activeVariant !== 'variant' && (
				<GetStartedLanding
					onComplete={handleLandingComplete}
					collectionProducts={collectionProducts}
					pageContent={pageContent}
				/>
			)}

			{currentStep === 'survey' && (
				<GetStartedSurvey onComplete={handleSurveyComplete} announcement={announcement} pageContent={pageContent} />
			)}

			{currentStep === 'auth' && (
				<GetStartedAuth onComplete={handleAuthComplete} announcement={announcement} offer={activeOffer} pageContent={pageContent} />
			)}

			{currentStep === 'plan' && (
				<GetStartedPlanSelection
					onComplete={checkoutEnabled ? handlePlanComplete : undefined}
					announcement={announcement}
					offer={activeOffer}
					pageContent={pageContent}
				/>
			)}

			{currentStep === 'checkout' && checkoutEnabled && (
				<div className="container px-4 py-8">
					<GetStartedCheckout />
				</div>
			)}
		</div>
	)
}
